import { Component } from '@theme/component';

const MAX_COPY_SETS = 50;

/**
 * A continuously scrolling press-logo marquee.
 *
 * Clones the original section blocks until the moving track is wide enough
 * to cross the viewport without exposing an empty region.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The visible marquee window.
 * @property {HTMLElement} track - The moving logo track.
 *
 * @extends {Component<Refs>}
 */
class AsSeenInMarqueeComponent extends Component {
  requiredRefs = ['viewport', 'track'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #resizeFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#rebuildTrack();
    this.#resizeObserver = new ResizeObserver(this.#scheduleRebuild);
    this.#resizeObserver.observe(this.refs.viewport);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRebuild();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#resizeFrame !== null) {
      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = null;
    }
  }

  #scheduleRebuild = () => {
    if (this.#resizeFrame !== null) return;

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = null;
      this.#rebuildTrack();
    });
  };

  #rebuildTrack() {
    const { track, viewport } = this.refs;

    this.removeAttribute('data-ready');

    for (const clone of track.querySelectorAll('[data-logo-clone]')) {
      clone.remove();
    }

    const originals = Array.from(track.querySelectorAll(':scope > [data-original-logo]')).filter(
      (logo) => logo instanceof HTMLElement
    );

    if (originals.length === 0 || viewport.clientWidth === 0) return;

    const firstClone = this.#appendCopy(originals)[0];
    const firstOriginal = originals[0];

    if (!firstClone || !firstOriginal) return;

    const loopDistance = firstClone.offsetLeft - firstOriginal.offsetLeft;

    if (loopDistance <= 0) return;

    let copySets = 2;
    while (track.scrollWidth < viewport.clientWidth + loopDistance && copySets < MAX_COPY_SETS) {
      this.#appendCopy(originals);
      copySets += 1;
    }

    this.style.setProperty('--as-seen-in-loop-offset', `${loopDistance * -1}px`);
    this.setAttribute('data-ready', '');
  }

  /**
   * @param {HTMLElement[]} originals
   * @returns {HTMLElement[]}
   */
  #appendCopy(originals) {
    const clones = [];

    for (const original of originals) {
      const clone = /** @type {HTMLElement} */ (original.cloneNode(true));

      clone.removeAttribute('ref');
      clone.removeAttribute('data-original-logo');
      clone.removeAttribute('id');
      clone.setAttribute('data-logo-clone', '');
      clone.setAttribute('aria-hidden', 'true');

      const cloneImages =
        clone instanceof HTMLImageElement ? [clone] : Array.from(clone.querySelectorAll('img'));

      for (const image of cloneImages) {
        image.alt = '';
      }

      if (clone instanceof HTMLAnchorElement) {
        clone.removeAttribute('href');
        clone.removeAttribute('target');
        clone.removeAttribute('rel');
      }

      for (const attribute of Array.from(clone.attributes)) {
        if (attribute.name.startsWith('data-shopify-editor-')) {
          clone.removeAttribute(attribute.name);
        }
      }

      this.refs.track.appendChild(clone);
      clones.push(clone);
    }

    return clones;
  }
}

if (!customElements.get('as-seen-in-marquee-component')) {
  customElements.define('as-seen-in-marquee-component', AsSeenInMarqueeComponent);
}
